// Keyboard bindings that live at the host-element level (not on individual
// controls, which have their own native handling). Currently: Space toggles
// play/pause anywhere inside the widget that isn't an <input type=range> or a
// text input (to avoid hijacking slider keyboard handling).

export interface KeyboardHostBindings {
  onTogglePlay: () => void;
}

export function installKeyboardHandlers(
  host: HTMLElement,
  bindings: KeyboardHostBindings,
): () => void {
  const listener = (e: KeyboardEvent) => {
    if (e.key !== ' ' && e.key !== 'Spacebar') return;

    const target = e.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      // Native space handling: buttons fire click, text inputs insert space.
      // Don't hijack these — they're handled per-control.
      if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') {
        return;
      }
    }

    // Only handle Space if it's relevant to this widget — focus is inside it,
    // or focus is on body (after we blurred, or nothing is focused).
    const path = e.composedPath();
    const inWidget = path.includes(host);
    const onBodyish = target === document.body || target === document.documentElement;
    if (!inWidget && !onBodyish) return;

    e.preventDefault();
    // Blur the target synchronously before toggling play. The host widget's
    // modality tracker flips to 'keyboard' on this same keydown (capture
    // phase), which would otherwise light up the focused canvas with a green
    // focus ring for a frame before onTogglePlay's async blur runs.
    target?.blur?.();
    bindings.onTogglePlay();
  };

  // Listen at the document level so subsequent Space presses still reach us
  // after we blurred the target (focus then lives on document.body).
  document.addEventListener('keydown', listener);
  return () => document.removeEventListener('keydown', listener);
}
